/**
 * Application Shell — Role-Adaptive Layout
 *
 * Fixed sidebar + header + scrollable main content area.
 * The sidebar content adapts based on the authenticated user's IC role.
 * Responsive: sidebar collapses to overlay on screens < 1024px.
 */

import { type ReactNode, useState, useCallback, useEffect } from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { AIChatWidget } from '@/components/AIChatWidget';
import { OnboardingTour } from '@/components/OnboardingTour';

interface ShellProps {
  children: ReactNode;
}

export function Shell({ children }: ShellProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showTour, setShowTour] = useState(false);

  // Auto-collapse sidebar on narrow viewports (<=1024px)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => setSidebarCollapsed(e.matches);
    mq.addEventListener('change', handler);
    if (mq.matches) setSidebarCollapsed(true);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const handleMenuToggle = useCallback(() => {
    // On large screens, toggle collapse. On small screens, toggle mobile overlay.
    if (window.innerWidth >= 1024) {
      setSidebarCollapsed((prev) => !prev);
    } else {
      setMobileOpen((prev) => !prev);
    }
  }, []);

  const handleMobileClose = useCallback(() => {
    setMobileOpen(false);
  }, []);

  // Auto-launch onboarding tour for first-time users
  useEffect(() => {
    const seen = localStorage.getItem('ic_tour_completed');
    if (!seen) {
      const timer = setTimeout(() => setShowTour(true), 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  const handleTourComplete = useCallback(() => {
    localStorage.setItem('ic_tour_completed', 'true');
    setShowTour(false);
  }, []);

  return (
    <div className="flex h-screen bg-ic-surface text-ic-text-primary">
      {/* Desktop sidebar — always visible on lg+ */}
      <div className="hidden lg:flex">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((prev) => !prev)}
        />
      </div>

      {/* Mobile sidebar overlay — visible only when mobileOpen on < lg */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
            onClick={handleMobileClose}
          />
          {/* Slide-over sidebar */}
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden">
            <Sidebar
              collapsed={false}
              onToggle={handleMobileClose}
              onNavigate={handleMobileClose}
            />
          </div>
        </>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <Header onMenuToggle={handleMenuToggle} onStartTour={() => setShowTour(true)} />

        <main className="flex-1 overflow-auto bg-ic-surface-secondary px-5 py-4">
          {children}
        </main>
      </div>

      {/* Floating AI Chat Widget */}
      <AIChatWidget />

      {/* Onboarding Tour */}
      <OnboardingTour run={showTour} onComplete={handleTourComplete} />
    </div>
  );
}
